/**
 * Library to calculate sample array of All leads.
 * @module leadCalc.js
 * @version 1.0.0
 * @author Kim Barnett
 * 
 * @param {object} ecg - ECG object with I, II, V1 (or) V2, V4 leads
 * 
 * @returns {object} leadData - Object with all leads, keys always in canonical
 *   clinical 12-lead order: I, II, III, aVR, aVL, aVF, V1(/V1-syn), V2(/V2-syn),
 *   V3(/V3-syn), V4, V5(/V5-syn), V6(/V6-syn). This insertion order is relied
 *   upon by ThreeRowView and AllLeadView, which derive column/row groupings
 *   directly from Object.keys(leadData).
 * 
 * @example
 * const ecg = {
 *     I: [1, 2, 3, 4, 5],
 *    II: [1, 2, 3, 4, 5],
 *    V1: [1, 2, 3, 4, 5],
 *    V4: [1, 2, 3, 4, 5]
 * };
 * const leadData = calculateLeads(ecg);
 * console.log(leadData);
 *  {
 *  I: [1, 2, 3, 4, 5],
 *  II: [1, 2, 3, 4, 5],
 *  III: [0, 0, 0, 0, 0],
 *  aVR: [0, 0, 0, 0, 0],
 *  aVL: [0, 0, 0, 0, 0],
 *  aVF: [0, 0, 0, 0, 0],
 *  V1: [1, 2, 3, 4, 5],
 *  V2-syn: [0, 0, 0, 0, 0],
 *  V3-syn: [0, 0, 0, 0, 0],
 *  V4: [1, 2, 3, 4, 5],
 *  V5-syn: [0, 0, 0, 0, 0],
 *  V6-syn: [0, 0, 0, 0, 0],
 *  }
 * 
*/

import * as math from 'mathjs';
 
function FnWithPerformance (data, leadIIIInput = undefined) {
    const start = performance.now()
    const returnVal = calculateLeads(data, leadIIIInput)
    const end = performance.now()
    window.printPerformance("Calculating Leads took:",`${(end-start).toFixed(2)}ms`);
    return returnVal
}


// Canonical 12-lead clinical display order.
// ThreeRowView and AllLeadView derive column/row groupings directly from
// Object.keys(samples), so this order determines the on-screen layout:
//   Column 1: I, II, III  |  Column 2: aVR, aVL, aVF
//   Column 3: V1, V2, V3  |  Column 4: V4, V5, V6
// Each V slot accepts either a measured name (e.g. 'V1') or a synthesised
// name (e.g. 'V1-syn') — the '-syn' suffix signals FullWidthECGPreview to
// render the synthesised-lead indicator.
const CANONICAL_ORDER = ['I', 'II', 'III', 'aVR', 'aVL', 'aVF', 'V1', 'V2', 'V3', 'V4', 'V5', 'V6'];

const calculateLeads = (ecg, leadIIIInput = undefined) => {
    const { I: leadI, II: leadII, V1: leadV1, V2: leadV2, V3: leadV3, V4: leadV4, V5: leadV5, V6: leadV6 } = ecg;

    // Collect all computed leads into a plain map first (key order doesn't
    // matter here — ordering is enforced at the end via CANONICAL_ORDER).
    const computed = {};

    // Echo every truthy input the caller gave us. Derived leads are layered on
    // top below. This guarantees the caller never loses the leads they supplied,
    // even when no derivation branch's preconditions are met.
    if (leadI)  computed['I']  = leadI;
    if (leadII) computed['II'] = leadII;
    if (leadV1) computed['V1'] = leadV1;
    if (leadV2) computed['V2'] = leadV2;
    if (leadV3) computed['V3'] = leadV3;
    if (leadV4) computed['V4'] = leadV4;
    if (leadV5) computed['V5'] = leadV5;
    if (leadV6) computed['V6'] = leadV6;

    // Calculate Leads III, aVR, aVL, aVF from Leads I, II.
    // If caller supplies leadIIIInput (e.g., LA/RA-reversal correction where
    // measured Lead II is physiologically true Lead III), use it directly.
    if (leadI && leadII) {
        computed['III'] = leadIIIInput !== undefined ? leadIIIInput : math.subtract(leadII, leadI);
        computed['aVR'] = math.multiply(-0.5, math.add(leadI, leadII));
        computed['aVL'] = math.subtract(leadI, math.multiply(0.5, leadII));
        computed['aVF'] = math.subtract(leadII, math.multiply(0.5, leadI));
    }

    if (!(leadV1 && leadV2 && leadV3 && leadV4 && leadV5 && leadV6)) {
        if (leadI && leadII && leadV2 && leadV4) {
            // Calculate Leads V1, V3, V5, V6 from Leads I, II, V2, V4
            const inputLeads = math.transpose(math.matrix([leadI, leadII, leadV2, leadV4]));
            const WLS4x4 = math.matrix([
                [-0.5863866, 0.12382167, 0.28485256, 0.39619061],
                [-0.12291817, -0.06476317, 0.30724672, 0.50331135],
                [0.4798221, 0.6032773, -0.13168489, -0.13499239],
                [-0.00942712, 0.6233477, 0.61597792, 0.23216768],
            ]);
            const vLeads = math.multiply(inputLeads, WLS4x4);
            const [rows] = vLeads.size();
            computed['V1-syn'] = vLeads.subset(math.index(math.range(0, rows), 0)).toArray().flat();
            computed['V3-syn'] = vLeads.subset(math.index(math.range(0, rows), 1)).toArray().flat();
            computed['V5-syn'] = vLeads.subset(math.index(math.range(0, rows), 2)).toArray().flat();
            computed['V6-syn'] = vLeads.subset(math.index(math.range(0, rows), 3)).toArray().flat();

        } else if (leadI && leadII && leadV1 && leadV4) {
            // Calculate Leads V2, V3, V5, V6 from Leads I, II, V1, V4
            const inputLeads = math.transpose(math.matrix([leadI, leadII, leadV1, leadV4]));
            const WLS4x4 = math.matrix([
                [0.62950665, 0.47780008, 0.21043114, 0.31790918],
                [-0.13829798, -0.16529997, 0.33109493, 0.52642836],
                [1.44278785, 0.84251819, -0.18082757, -0.18753116],
                [0.31313108, 0.82497713, 0.57055053, 0.18659219],
            ]);
            const vLeads = math.multiply(inputLeads, WLS4x4);
            const [rows] = vLeads.size();
            computed['V2-syn'] = vLeads.subset(math.index(math.range(0, rows), 0)).toArray().flat();
            computed['V3-syn'] = vLeads.subset(math.index(math.range(0, rows), 1)).toArray().flat();
            computed['V5-syn'] = vLeads.subset(math.index(math.range(0, rows), 2)).toArray().flat();
            computed['V6-syn'] = vLeads.subset(math.index(math.range(0, rows), 3)).toArray().flat();
        }
    }

    // Build the final object in canonical clinical order.
    // For each canonical slot (e.g. 'V1') we check for both the measured key
    // ('V1') and the synthesised key ('V1-syn') so that the slot always appears
    // in the correct column position regardless of which variant is present.
    // JavaScript preserves string-key insertion order (ES2015+).
    const leadData = {};
    for (const canonicalKey of CANONICAL_ORDER) {
        if (computed[canonicalKey] !== undefined) {
            leadData[canonicalKey] = computed[canonicalKey];
        } else if (computed[`${canonicalKey}-syn`] !== undefined) {
            leadData[`${canonicalKey}-syn`] = computed[`${canonicalKey}-syn`];
        }
    }

    return leadData;
}

export default FnWithPerformance;